<!--
  GoogleSignIn.vue
  Vue 3 component for Google Sign-In button

  Features:
  - Renders Google Sign-In button using Google Identity Services
  - Handles OAuth callback and sends token to backend
  - Emits success, linkRequired, and error events

  Props: None

  Emits:
  - success: When login is successful, passes user data
  - linkRequired: When email exists with password auth, passes email and credential
  - error: When an error occurs, passes error message

  Usage:
  <GoogleSignIn
    @success="handleLoginSuccess"
    @link-required="handleLinkRequired"
    @error="handleError"
  />
-->

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { CredentialResponse } from '../../types/google.d.ts'

// Emits
const emit = defineEmits<{
  success: [user: unknown]
  linkRequired: [email: string, credential: string]
  error: [message: string]
}>()

// Component state
const loading = ref(false)
const isInitialized = ref(false)

// API URL configuration
const isDevPort = window.location.port && !['80', '443', ''].includes(window.location.port)
const API_URL = (window.location.hostname === 'localhost' && isDevPort)
  ? `${window.location.protocol}//localhost:8000/api`
  : `${window.location.origin}/api`

// Google Client ID from environment variable
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

/**
 * Handle the credential response from Google Sign-In
 */
async function handleGoogleResponse(response: CredentialResponse) {
  if (!response.credential) {
    emit('error', 'No credential received from Google')
    return
  }

  loading.value = true

  try {
    const result = await fetch(`${API_URL}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential: response.credential }),
    })

    const data = await result.json()

    if (!result.ok) {
      // Check for EMAIL_EXISTS error (account linking needed)
      if (data.detail?.code === 'EMAIL_EXISTS') {
        emit('linkRequired', data.detail.email, response.credential)
        return
      }

      // Handle other errors
      const errorMsg = typeof data.detail === 'string'
        ? data.detail
        : data.detail?.message || data.message || 'Google login failed'
      throw new Error(errorMsg)
    }

    // Store tokens
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)

    // Emit success
    emit('success', data.user)
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Google login failed'
    emit('error', errorMessage)
  } finally {
    loading.value = false
  }
}

/**
 * Initialize Google Sign-In on mount
 */
onMounted(() => {
  if (!GOOGLE_CLIENT_ID) {
    console.warn('[GoogleSignIn] VITE_GOOGLE_CLIENT_ID not configured - Google Sign-In disabled')
    return
  }

  // Wait for Google SDK to load
  const initGoogle = () => {
    if (!window.google?.accounts?.id) {
      // SDK not loaded yet, retry
      setTimeout(initGoogle, 100)
      return
    }

    // Initialize Google Sign-In
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    })

    // Render the button
    const buttonElement = document.getElementById('google-signin-button')
    if (buttonElement) {
      window.google.accounts.id.renderButton(buttonElement, {
        theme: 'filled_blue',
        size: 'large',
        width: '100%',
        text: 'continue_with',
        shape: 'rectangular',
      })
      isInitialized.value = true
    }
  }

  initGoogle()
})
</script>

<template>
  <div class="google-signin">
    <!-- Google renders the button here -->
    <div
      v-show="GOOGLE_CLIENT_ID && isInitialized"
      id="google-signin-button"
      :class="{ 'is-loading': loading }"
    ></div>

    <!-- Loading overlay -->
    <div v-if="loading" class="loading-overlay">
      <span>Signing in with Google...</span>
    </div>

    <!-- Disabled state when no client ID -->
    <div v-if="!GOOGLE_CLIENT_ID" class="google-disabled">
      <span class="material-symbols-outlined">cloud_off</span>
      <span>Google Sign-In not configured</span>
    </div>
  </div>
</template>

<style scoped>
.google-signin {
  position: relative;
  width: 100%;
  min-height: 44px;
}

#google-signin-button {
  width: 100%;
  display: flex;
  justify-content: center;
}

#google-signin-button.is-loading {
  opacity: 0.5;
  pointer-events: none;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border-radius: 4px;
  font-size: 0.875rem;
  z-index: 10;
}

.google-disabled {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--bg-secondary, #f3f4f6);
  border-radius: 8px;
  color: var(--text-secondary, #6b7280);
  font-size: 0.875rem;
}

.google-disabled .material-symbols-outlined {
  font-size: 1.25rem;
}
</style>
